import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';

import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { DropdownFilterValue } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { UserStatusEnum } from 'shared/constants/user-status-enum';
import { ColoredPillTypesEnum } from 'shared/enums/colored-pill-types.enum';
import { EventBusService } from 'shared/services/event-bus.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { User } from 'users/interfaces/user.interface';
import { ShijiUsersService } from 'users/services/shiji-users.service';
import { ShijiUserEditComponent } from '../shiji-user-edit/shiji-user-edit.component';
import { ShijiUserInviteComponent } from '../shiji-user-invite/shiji-user-invite.component';

@Component({
    selector: 'app-shiji-users-table',
    templateUrl: './shiji-users-table.component.html',
    styleUrls: ['./shiji-users-table.component.scss'],
    imports: [AgGridModule, TranslateModule],
})
export class ShijiUsersTableComponent extends BaseGrid implements OnInit {
    public columnDefs: ColDef[] = [
        {
            field: 'email',
            unSortIcon: true,
            floatingFilter: true,
            width: 200,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'firstName',
            unSortIcon: true,
            floatingFilter: true,
            width: 150,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'lastName',
            width: 150,
            unSortIcon: true,
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'status',
            unSortIcon: true,
            width: 160,
            valueGetter: this.statusFormatter,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: Object.keys(UserStatusEnum).map((key) => {
                    return {
                        label: key.toSentenceCase(),
                        value: key,
                    } as DropdownFilterValue;
                }) as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            cellRenderer: 'coloredPillCellRenderer',
            cellRendererParams: {
                pillType: (params: string) => {
                    const pillTypesMap = new Map();
                    pillTypesMap.set('active', ColoredPillTypesEnum.SUCCESS);
                    pillTypesMap.set('locked', ColoredPillTypesEnum.WARNING);
                    pillTypesMap.set('disabled', ColoredPillTypesEnum.NEUTRAL);

                    return pillTypesMap.get(params.toLowerCase());
                },
            },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'lastLogin',
            valueFormatter: this.dateFormatter,
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            resizable: false,
            width: 150,
            floatingFilterComponent: DateFloatingFilterComponent,
            unSortIcon: true,
        },
        {
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                buttons: [
                    {
                        clickAction: this.deleteUserButtonClickHandler.bind(this),
                        icon: 'trash',
                        tooltip: 'generalTooltips.delete',
                    },
                    {
                        clickAction: this.editUserButtonClickHandler.bind(this),
                        icon: 'pen-to-square',
                        tooltip: 'generalTooltips.edit',
                    },
                ],
            },
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private usersGridService: ShijiUsersService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        private modalService: ModalService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(usersGridService, router, activatedRoute, destroyRef);
    }

    ngOnInit() {
        this.eventBusService
            .getObservableForEventStream('pageMainButtonAction')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                if (data === 'shijiUsers') {
                    this.openInviteShijiUserModal();
                }
            });
    }

    public editUserButtonClickHandler(rowData: User) {
        const modalData = { ...rowData, type: 'side' };
        this.modalService
            .openModal(ShijiUserEditComponent, modalData)
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                if (isSaved) {
                    this.agGrid.api.refreshServerSide({ purge: true });
                }
            });
    }

    public deleteUserButtonClickHandler(rowData: User) {
        this.modalService
            .openDeleteConfirmationModal(
                this.translateService.instant('users.shijiUsers.deleteModal.body'),
                this.translateService.instant('users.shijiUsers.deleteModal.title'),
                undefined,
                rowData.email,
                this.translateService.instant('users.shijiUsers.deleteModal.entityTypeName'),
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => {
                    return this.usersGridService.deleteShijiUser(rowData);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('users.shijiUsers.deleteSuccess', 'success', { user: rowData.email });
                this.agGrid.api.refreshServerSide({ purge: true });
            });
    }

    private openInviteShijiUserModal() {
        this.modalService
            .openModal(ShijiUserInviteComponent, { type: 'side' })
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                //We don't need to refresh the grid since the user won't appear in the table after being invited, but only after they register
                if (isSaved && !this.agGrid) {
                    this.anyRows.set(true);
                }
            });
    }

    private statusFormatter(params: ValueGetterParams) {
        return params.data.status.toString().toSentenceCase();
    }
}
